import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "primereact/button";
import { useNavigate, useParams } from "react-router-dom";
import { FormFieldsPanel } from "../../../../components/form-fields-panel/form-fields-panel";
import { If } from "../../../../components/If/If";
import { LabelValue } from "../../../../components/LabelValue/LabelValue";
import { Loader } from "../../../../components/Loader/Loader";
import { SectionHeader } from "../../../../components/SectionHeader/SectionHeader";
import { requests } from "../../../../services/http-common";
import { useToast } from "../../../../Providers/ToastProvider";
import { CustomerCategoryEnum } from "../../SalesLead/Lead.model";
import { IClient } from "../Client.model";
import classes from "./ClientDetails.module.scss";
import { PrivateAccess, useKeycloakContext } from "../../../../keycloak";
import { UserRoles } from "../../../../constants/userRoles";
import WelcomeModal from "./WelcomeModal";
import { useState } from "react";

interface ClientDetailsProps {
  isOwn?: boolean;
}

export const ClientDetails = ({ isOwn }: ClientDetailsProps) => {
  let { clientId } = useParams<{ clientId: string }>();

  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  const keycloakContexxt = useKeycloakContext();
  const subId = keycloakContexxt.keycloak?.tokenParsed?.sub;

  if (isOwn) {
    clientId = subId;
  }

  const navigate = useNavigate();
  const { show } = useToast();

  const sendWelcome = useMutation({
    mutationFn: () => {
      return requests.post(`/client/${clientId}/send-welcome-email`, {});
    },
    onSuccess: () => {
      show({ detail: "Welcome email was sent successfully !" });
    },
    onError: (error: any) => {
      show({
        severity: "error",
        detail: error.response?.data?.error?.message || "Something went wrong ",
      });
    },
  });

  const { data, isLoading } = useQuery<IClient>({
    queryKey: [`client-details-${clientId}`],
    queryFn: () => {
      return requests.get(`/client/${clientId}`);
    },
    onSuccess: (data: any) => {
      if (data.isFirstSignIn) {
        setShowWelcomeModal(true);
      }
    },
  });

  if (isLoading || !data) {
    return <Loader />;
  }

  return (
    <>
      <div className={classes.container}>
        <SectionHeader
          title={isOwn ? "Profile" : "Client details"}
          showBackButton={!isOwn}
        />
        <PrivateAccess
          roles={{
            dcs_client: [UserRoles.CLIENT_UPDATE],
          }}
        >
          <div className={classes.actionBar}>
            <Button
              label="Edit"
              className="p-button-outlined p-button-secondary"
              onClick={() => navigate(`edit`)}
            />
            {!isOwn && (
              <Button
                label="Send welcome email"
                className="p-button-outlined p-button-secondary"
                loading={sendWelcome.isLoading}
                onClick={() => sendWelcome.mutate()}
              />
            )}
          </div>
        </PrivateAccess>
        <div className={classes.content}>
          <FormFieldsPanel
            label="General information"
            className={classes.employeeAccount}
          >
            <div className={classes.group}>
              <LabelValue bold label="Title">
                {data.title || "-"}
              </LabelValue>
              <If
                condition={
                  data.customerCategory === CustomerCategoryEnum.CORPORATE
                }
              >
                <>
                  <LabelValue bold label="Company name">
                    {data.companyName || "-"}
                  </LabelValue>
                  <LabelValue bold label="Contact name">
                    {data.contactName || "-"}
                  </LabelValue>
                </>
                <>
                  <LabelValue bold label="First name">
                    {data.name || "-"}
                  </LabelValue>
                  <LabelValue bold label="Last name">
                    {data.surname || "-"}
                  </LabelValue>
                </>
              </If>
              {/* <LabelValue label="Display name as">
              {data.displayNameAs.map()}
            </LabelValue> */}
              <LabelValue bold label="Email">
                {data.email || "-"}
              </LabelValue>
              <LabelValue bold label="Phone">
                {data.phone || "-"}
              </LabelValue>
              <LabelValue bold label="Mobile">
                {data.mobile || "-"}
              </LabelValue>
              <LabelValue bold label="Fax">
                {data.fax || "-"}
              </LabelValue>
              <LabelValue bold label="Website">
                {data.website ? (
                  <a
                    target="_blank"
                    rel="noreferrer"
                    href={
                      data.website.startsWith("http://") ||
                      data.website.startsWith("https://")
                        ? data.website
                        : `http://${data.website}`
                    }
                  >
                    {" "}
                    {data.website}{" "}
                  </a>
                ) : (
                  "-"
                )}
              </LabelValue>
              <LabelValue bold label="Address details">
                {data.address || "-"}
              </LabelValue>
            </div>
            {data.displayNameAs?.length ? (
              <div className={classes.displayNameAsTitle}>
                <h4>Display name as</h4>
                <LabelValue bold label="Primary">
                  <p>{data.displayNameAs[0].displayName}</p>
                </LabelValue>
                <LabelValue bold label="Secondary">
                  {data.displayNameAs.length > 1 ? (
                    <>
                      {data.displayNameAs
                        .filter((_, i) => i > 0)
                        .map((d) => (
                          <p key={d.displayNameId}>{d.displayName}</p>
                        ))}
                    </>
                  ) : (
                    "-"
                  )}
                </LabelValue>
              </div>
            ) : null}
          </FormFieldsPanel>
        </div>
      </div>
      <WelcomeModal
        showModal={showWelcomeModal}
        setModalState={setShowWelcomeModal}
      />
    </>
  );
};
